import { NEEDS_AI_DRAFT_BODY } from "@/lib/journey-touch-display";
import type { JourneyPlanItem } from "@/lib/chat-timeline";
import type { JourneyNode } from "@/lib/journey-api";
import { resolveStepBody, resolveStepInstruction, type JourneyPlanStep } from "@/lib/journey-bindings";
import type { WecomInboundEvent } from "@/lib/wecom-inbound-events";
import type { WecomOutboundMessage } from "@/lib/wecom-outbound-messages";
import { buildChatTimelineItems, type ChatTimelineItem } from "@/lib/chat-timeline";

export const EMPTY_NEXT_COPY = {
  noBinding: "未加入旅程",
  noLive: "没有待办步骤",
  allInFlight: "所有步骤等待企微确认",
} as const;

const LIVE_STATUSES = new Set(["planned", "queued"]);

export function handbookOrdinal(
  nodes: Pick<JourneyNode, "id" | "offset_days">[],
  nodeId: number | null | undefined,
): number | null {
  if (nodeId == null) return null;
  const sorted = nodes
    .filter((n): n is typeof n & { id: number } => n.id != null)
    .sort((a, b) => {
      const diff = a.offset_days - b.offset_days;
      if (diff !== 0) return diff;
      return a.id - b.id;
    });
  const index = sorted.findIndex((n) => n.id === nodeId);
  return index >= 0 ? index + 1 : null;
}

export function isPlaceholderDraft(opts: {
  needsAiDraft?: boolean;
  draft?: string | null;
  instruction?: string | null;
}): boolean {
  if (opts.needsAiDraft) return true;
  const draft = (opts.draft ?? "").trim();
  if (!draft || draft === NEEDS_AI_DRAFT_BODY) return true;
  const instruction = (opts.instruction ?? "").trim();
  return Boolean(instruction) && draft === instruction;
}

/** 轻插入档点落点：≥ now 的下一档；已过今日全部档 → 次日首档 */
export function insertPlannedAt(now: Date, slots: string[] = ["10:00", "14:00", "18:00"]): Date {
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const slotMinutes = slots.map((slot) => {
    const [hours, minutes] = slot.split(":").map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
  });
  const found = slotMinutes.find((value) => value >= minutesNow);
  const result = new Date(now);
  if (found != null) {
    result.setHours(Math.floor(found / 60), found % 60, 0, 0);
    return result;
  }
  const first = slotMinutes[0] ?? 10 * 60;
  result.setDate(result.getDate() + 1);
  result.setHours(Math.floor(first / 60), first % 60, 0, 0);
  return result;
}

export function emptyNextCopy(opts: {
  hasBinding: boolean;
  liveCount: number;
  inFlightCount: number;
}): string | null {
  if (!opts.hasBinding) return EMPTY_NEXT_COPY.noBinding;
  if (opts.liveCount === 0) return EMPTY_NEXT_COPY.noLive;
  if (opts.inFlightCount === opts.liveCount) return EMPTY_NEXT_COPY.allInFlight;
  return null;
}

export function planCardTitle(opts: {
  planStepId: number;
  nextStepId: number | null;
  ordinal: number | null;
}): string {
  const stepHint = opts.ordinal != null ? `第 ${opts.ordinal} 步` : "计划";
  if (opts.nextStepId != null && opts.planStepId === opts.nextStepId) {
    return `下一步 · ${stepHint}`;
  }
  return `后续 · ${stepHint}`;
}

function hasSentOutbound(outbounds: WecomOutboundMessage[], stepId: number): boolean {
  return outbounds.some((m) => m.journey_plan_step_id === stepId && m.status === "sent");
}

function isInFlight(outbounds: WecomOutboundMessage[], stepId: number): boolean {
  const latest = outbounds
    .filter((m) => m.journey_plan_step_id === stepId)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
  return latest?.status === "pending_confirm";
}

export type TimelineZonesResult = {
  happened: ChatTimelineItem[];
  queue: JourneyPlanItem[];
  preview: JourneyPlanItem[];
  nextStepId: number | null;
  emptyCopy: string | null;
  liveCount: number;
  inFlightCount: number;
};

/**
 * 已发生 ∥ 接下来。接下来按手册序；在途步不进 queue。
 */
export function timelineZones(opts: {
  inboundEvents: WecomInboundEvent[];
  outboundMessages: WecomOutboundMessage[];
  steps: JourneyPlanStep[];
  nodes: Pick<JourneyNode, "id" | "offset_days">[];
  planMeta: { journeyId: number; journeyName: string };
  hasBinding: boolean;
  showAllFuture: boolean;
  now?: Date;
}): TimelineZonesResult {
  const happened = buildChatTimelineItems({
    inboundEvents: opts.inboundEvents,
    outboundMessages: opts.outboundMessages,
    futurePlans: [],
  });

  if (!opts.hasBinding) {
    return {
      happened,
      queue: [],
      preview: [],
      nextStepId: null,
      emptyCopy: EMPTY_NEXT_COPY.noBinding,
      liveCount: 0,
      inFlightCount: 0,
    };
  }

  const live = opts.steps.filter((step) => {
    if (!LIVE_STATUSES.has(step.status)) return false;
    if (!step.should_send && !step.needs_ai_draft) return false;
    if (hasSentOutbound(opts.outboundMessages, step.id)) return false;
    return true;
  });

  const inFlight = live.filter((step) => isInFlight(opts.outboundMessages, step.id));
  const inFlightIds = new Set(inFlight.map((s) => s.id));
  const minLive = [...live].sort((a, b) => {
    const oa = handbookOrdinal(opts.nodes, a.journey_node_id) ?? 999;
    const ob = handbookOrdinal(opts.nodes, b.journey_node_id) ?? 999;
    if (oa !== ob) return oa - ob;
    return a.id - b.id;
  })[0];
  const minLiveInFlight = minLive != null && inFlightIds.has(minLive.id);

  const queueSteps = live
    .filter((step) => !inFlightIds.has(step.id))
    .sort((a, b) => {
      const oa = handbookOrdinal(opts.nodes, a.journey_node_id) ?? 999;
      const ob = handbookOrdinal(opts.nodes, b.journey_node_id) ?? 999;
      if (oa !== ob) return oa - ob;
      return a.position - b.position;
    });

  const nextStepId = minLive && !minLiveInFlight ? minLive.id : null;

  const queue: JourneyPlanItem[] = queueSteps.map((step) => {
    const ordinal = handbookOrdinal(opts.nodes, step.journey_node_id);
    const scheduled =
      step.planned_at ||
      (step.planned_on ? `${step.planned_on}T00:00:00` : new Date().toISOString());
    const today = startOfLocalDay(opts.now ?? new Date());
    const plannedDay = parsePlannedOn(step.planned_on) ?? today;
    const offsetDays = Math.round((plannedDay.getTime() - today.getTime()) / 86_400_000);
    const needsAi = Boolean(step.needs_ai_draft) || isPlaceholderDraft({
      needsAiDraft: step.needs_ai_draft,
      draft: step.draft_content,
      instruction: step.instruction ?? step.task_brief,
    });
    return {
      id: `plan-step-${step.id}`,
      journeyId: opts.planMeta.journeyId,
      journeyName: opts.planMeta.journeyName,
      body: resolveStepBody(step, needsAi),
      instruction: resolveStepInstruction(step),
      offsetDays,
      scheduledAt: scheduled,
      stepStatus: step.status,
      position: step.position,
      planStepId: step.id,
      preview: false,
      needsAiDraft: needsAi,
      journeyNodeId: step.journey_node_id,
      handbookOrdinal: ordinal,
    };
  });

  const preview = opts.showAllFuture ? queue : queue.slice(0, 2);
  const emptyCopy = emptyNextCopy({
    hasBinding: true,
    liveCount: live.length,
    inFlightCount: inFlight.length,
  });

  return {
    happened,
    queue,
    preview,
    nextStepId,
    emptyCopy,
    liveCount: live.length,
    inFlightCount: inFlight.length,
  };
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parsePlannedOn(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
